/**
 * @file Duplicate upload files page.
 * @description Lists duplicate lead upload CSV files with search, pagination, and download.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, Copy, Download, FileText, RefreshCw, Search, X } from 'lucide-react';
import { LoadingSpinner, PageHeader, Pagination, Panel } from '../components/ui';
import { PAGINATION } from '../constants/api';
import type { DuplicateFile } from '../types/api/duplicateFiles';
import { DuplicateFilesService } from '../services/duplicateFilesService';

const PER_PAGE = PAGINATION.DEFAULT_PER_PAGE;
const TH = 'px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider';
const TD = 'px-4 py-4 text-sm text-gray-900 align-top';

const DuplicateFiles: React.FC = () => {
  const [files, setFiles] = useState<DuplicateFile[]>([]);
  const [allFiles, setAllFiles] = useState<DuplicateFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageSize, setPageSize] = useState<number>(PER_PAGE);
  const [serverPaginated, setServerPaginated] = useState(true);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const initialFetchDoneRef = useRef(false);
  const searchDebounceRef = useRef<number | null>(null);
  const activeSearchRef = useRef('');

  const applyClientPage = useCallback((rows: DuplicateFile[], page: number) => {
    const total = rows.length;
    const pages = Math.max(1, Math.ceil(total / PER_PAGE));
    const safePage = Math.min(Math.max(1, page), pages);
    const start = (safePage - 1) * PER_PAGE;

    setAllFiles(rows);
    setFiles(rows.slice(start, start + PER_PAGE));
    setCurrentPage(safePage);
    setTotalPages(pages);
    setTotalRecords(total);
    setPageSize(PER_PAGE);
  }, []);

  const fetchFiles = useCallback(
    async (page: number = 1, search: string = activeSearchRef.current) => {
      const isFirstPaint = !initialFetchDoneRef.current;
      setErrorMsg(null);

      if (isFirstPaint) {
        setLoading(true);
      } else {
        setRefreshing(true);
      }

      try {
        const result = await DuplicateFilesService.listFiles(page, PER_PAGE, search);
        const hasServerPaging =
          result.pagination.total > result.files.length ||
          result.pagination.total_page > 1 ||
          result.pagination.page === page;

        setServerPaginated(hasServerPaging);

        if (hasServerPaging) {
          setFiles(result.files);
          setAllFiles(result.files);
          setCurrentPage(result.pagination.page);
          setTotalPages(result.pagination.total_page);
          setTotalRecords(result.pagination.total);
          setPageSize(result.pagination.per_page || PER_PAGE);
        } else {
          applyClientPage(result.files, page);
        }
      } catch (error) {
        setErrorMsg(error instanceof Error ? error.message : 'Failed to load duplicate files');
        setFiles([]);
        setAllFiles([]);
        setTotalRecords(0);
        setTotalPages(1);
      } finally {
        setLoading(false);
        setRefreshing(false);
        initialFetchDoneRef.current = true;
      }
    },
    [applyClientPage],
  );

  useEffect(() => {
    void fetchFiles(1);
  }, [fetchFiles]);

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current !== null) {
        window.clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  const handlePageChange = useCallback(
    (page: number) => {
      if (page < 1 || page > totalPages || page === currentPage || refreshing) {
        return;
      }

      if (serverPaginated) {
        void fetchFiles(page, activeSearchRef.current);
        return;
      }

      applyClientPage(allFiles, page);
    },
    [
      allFiles,
      applyClientPage,
      currentPage,
      fetchFiles,
      refreshing,
      serverPaginated,
      totalPages,
    ],
  );

  const applySearch = useCallback(
    (query: string) => {
      activeSearchRef.current = query.trim();
      void fetchFiles(1, activeSearchRef.current);
    },
    [fetchFiles],
  );

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchQuery(value);
      if (searchDebounceRef.current !== null) {
        window.clearTimeout(searchDebounceRef.current);
      }
      searchDebounceRef.current = window.setTimeout(() => {
        applySearch(value);
      }, 300);
    },
    [applySearch],
  );

  const clearSearch = useCallback(() => {
    setSearchQuery('');
    if (searchDebounceRef.current !== null) {
      window.clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }
    applySearch('');
  }, [applySearch]);

  const handleDownload = useCallback(async (file: DuplicateFile) => {
    setDownloadingId(file.id);
    try {
      const { blob, filename } = await DuplicateFilesService.downloadFile(
        file,
        file.filename || `duplicate-${file.id}.csv`,
      );
      DuplicateFilesService.triggerDownload(blob, filename);
    } catch (error) {
      setErrorMsg(error instanceof Error ? error.message : 'Failed to download file');
    } finally {
      setDownloadingId(null);
    }
  }, []);

  const displayValue = useCallback((value: string) => {
    const text = value.trim();
    return text || 'N/A';
  }, []);

  const serialOffset = useMemo(
    () => (currentPage - 1) * pageSize,
    [currentPage, pageSize],
  );

  const isFetchError = !loading && Boolean(errorMsg) && files.length === 0;
  const isEmpty = !loading && !errorMsg && files.length === 0;
  const hasData = !loading && files.length > 0;

  const handleRetry = () => {
    initialFetchDoneRef.current = false;
    setErrorMsg(null);
    void fetchFiles(1, activeSearchRef.current);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Duplicate File"
        subtitle="View and download duplicate lead upload files"
        icon={Copy}
        className="shadow-md"
      />

      <Panel padded className="min-h-[300px] p-0">
        <div className="flex flex-col gap-3 border-b border-gray-200 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <h3 className="shrink-0 text-lg font-semibold text-gray-900">All Duplicate Files</h3>
          <div className="relative w-full sm:max-w-xs sm:min-w-[16rem]">
            <Search
              size={18}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              aria-hidden
            />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder="Search by file name…"
              className="w-full rounded-lg border border-gray-300 bg-white py-2.5 pl-10 pr-10 text-sm text-gray-900 shadow-sm transition-colors placeholder:text-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
              aria-label="Search duplicate files"
            />
            {searchQuery.length > 0 && (
              <button
                type="button"
                onClick={clearSearch}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                aria-label="Clear search"
              >
                <X size={16} />
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-16 px-4">
            <LoadingSpinner size="lg" label="Loading duplicate files" />
            <p className="mt-4 text-sm font-medium text-gray-600">Loading duplicate files...</p>
          </div>
        ) : isFetchError ? (
          <div className="flex flex-col items-center justify-center px-4 py-14 sm:px-6">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-50">
              <AlertCircle size={32} className="text-red-500" aria-hidden />
            </div>
            <h4 className="mt-5 text-lg font-semibold text-gray-900">Failed to load duplicate files</h4>
            <p className="mt-2 max-w-md text-center text-sm text-gray-600">{errorMsg}</p>
            <button
              type="button"
              onClick={handleRetry}
              className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary-600 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-700"
            >
              <RefreshCw size={16} />
              Try Again
            </button>
          </div>
        ) : isEmpty ? (
          <div className="flex flex-col items-center justify-center px-4 py-14 sm:px-6">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gray-100">
              <FileText size={32} className="text-gray-400" aria-hidden />
            </div>
            <h4 className="mt-5 text-lg font-semibold text-gray-900">No duplicate files found</h4>
            <p className="mt-2 max-w-md text-center text-sm text-gray-600">
              {searchQuery.trim()
                ? 'No files match your search. Try a different file name.'
                : 'There are no duplicate upload files to display yet.'}
            </p>
          </div>
        ) : hasData ? (
          <>
            {errorMsg && (
              <div className="mx-4 mt-4 flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 sm:mx-6">
                <AlertCircle size={18} className="mt-0.5 shrink-0 text-red-500" aria-hidden />
                <div className="min-w-0 flex-1">
                  <p className="font-medium">Something went wrong</p>
                  <p className="mt-0.5">{errorMsg}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setErrorMsg(null)}
                  className="shrink-0 text-xs font-medium text-red-700 underline hover:text-red-800"
                >
                  Dismiss
                </button>
              </div>
            )}

            <div className="relative">
              {refreshing && (
                <div
                  className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-white/70 backdrop-blur-[1px]"
                  aria-busy="true"
                  aria-live="polite"
                >
                  <div className="flex items-center gap-2 rounded-lg bg-white px-4 py-2 shadow-md border border-gray-100">
                    <LoadingSpinner size="sm" centered={false} label="Updating duplicate files" />
                    <span className="text-sm font-medium text-gray-700">Updating…</span>
                  </div>
                </div>
              )}

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className={`${TH} text-center w-16`}>S.No</th>
                      <th className={TH}>File Name</th>
                      <th className={`${TH} text-right`}>Action</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {files.map((file, index) => (
                      <tr key={file.id} className="hover:bg-gray-50 transition-colors">
                        <td className={`${TD} text-center tabular-nums text-gray-600`}>
                          {serialOffset + index + 1}
                        </td>
                        <td className={TD}>
                          <div className="font-medium text-gray-900 break-words">
                            {displayValue(file.filename)}
                          </div>
                        </td>
                        <td className={`${TD} text-right whitespace-nowrap`}>
                          <button
                            type="button"
                            onClick={() => void handleDownload(file)}
                            disabled={downloadingId === file.id}
                            className="inline-flex items-center gap-2 rounded-md bg-gradient-to-r from-primary-500 to-secondary-500 px-3 py-2 text-xs font-medium text-white shadow-sm transition-all hover:from-primary-500 hover:to-secondary-500 disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            <Download size={14} />
                            {downloadingId === file.id ? 'Downloading…' : 'Download'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <Pagination
              className="mt-0"
              currentPage={currentPage}
              totalPages={totalPages}
              totalRecords={totalRecords}
              pageSize={pageSize}
              onPageChange={handlePageChange}
              itemLabel="files"
              disabled={refreshing}
              ariaLabel="Duplicate files pagination"
            />
          </>
        ) : null}
      </Panel>
    </div>
  );
};

export default DuplicateFiles;
